import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Row, Col, Typography, Statistic } from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import {
  InvestorsIcon,
  FileHubIcon,
  TeamIcon,
  MeetingIcon,
  SettingsIcon,
  TransactionHistoryIcon,
} from '@/common/components/custom-icons';
import {
  getUserData,
  getGoogleCalendarEvents,
  GoogleCalendarEventResponse,
  getConnectedGoogleAccounts,
  GetGoogleAccountResponse,
  getCorrespondentStats,
  CorrespondentStats,
  getInvestors,
} from '@/api/custom';
import QuickAccess, { type QuickAccessItem } from '@/common/components/quick-access';
import { useAppSelector } from '@/store/hooks';
import { selectProfileData, selectUser } from '@/store/features/userinfo/userinfoSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  buildPageLoaderBranding,
  buildInitialAppLoadPageLoaderMessages,
  DASHBOARD_DESTINATION_PAGE_LOADER_MESSAGES,
  DASHBOARD_PAGE_LOADER_MESSAGES,
  getAdvisorNameFromUser,
  WavvestPageLoader,
} from '@/common/components/page-loader';
import {
  clearInitialAppLoadState,
  getInitialAppLoadAdvisorName,
  getInitialAppLoadBranding,
  getInitialAppLoadStartedAt,
  getInitialAppLoadSuppressAdvisorName,
  hasInitialAppLoadState,
} from '@/routes/initial-app-load-state';
import './index.scss';

const { Text } = Typography;

// Define upcoming meeting interface
interface UpcomingMeeting {
  title: string;
  start_time: string;
  end_time: string;
}

interface DashboardProps {
  isProductionEnvironment?: boolean;
}

const parseDashboardStat = (value: string | number | null | undefined) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const INVESTOR_COUNT_PAGE_SIZE = 1024;

const getDashboardInitialLoadCheckpointMessages = ({
  loadingInvestors,
  loadingMeetings,
  loadingStats,
}: {
  loadingInvestors: boolean;
  loadingMeetings: boolean;
  loadingStats: boolean;
}): ReadonlyArray<string> => {
  if (loadingInvestors) {
    return DASHBOARD_DESTINATION_PAGE_LOADER_MESSAGES.slice(0, 1);
  }

  if (loadingMeetings) {
    return DASHBOARD_DESTINATION_PAGE_LOADER_MESSAGES.slice(0, 2);
  }

  if (loadingStats) {
    return DASHBOARD_DESTINATION_PAGE_LOADER_MESSAGES.slice(0, 3);
  }

  return DASHBOARD_DESTINATION_PAGE_LOADER_MESSAGES;
};

const isProductionDashboardEnvironment = () => {
  const prodValue: unknown = import.meta.env.PROD;
  return prodValue === true || prodValue === 'true';
};

const Dashboard: React.FC<DashboardProps> = ({
  isProductionEnvironment = isProductionDashboardEnvironment(),
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationHasInitialAppLoad = hasInitialAppLoadState(location.state);
  const [isInitialDashboardLoad] = useState(() => locationHasInitialAppLoad);
  const [initialDashboardLoaderStartedAt] = useState(() =>
    getInitialAppLoadStartedAt(location.state),
  );
  const [initialDashboardAdvisorName] = useState(() =>
    getInitialAppLoadAdvisorName(location.state),
  );
  const [initialDashboardBranding] = useState(() => getInitialAppLoadBranding(location.state));
  const [suppressInitialDashboardAdvisorName] = useState(() =>
    getInitialAppLoadSuppressAdvisorName(location.state),
  );
  const user = useAppSelector(selectUser);
  const profileData = useAppSelector(selectProfileData);
  const [totalInvestors, setTotalInvestors] = useState<number>(0);
  const [upcomingMeeting, setUpcomingMeeting] = useState<UpcomingMeeting | null>(null);
  const [totalMeetings, setTotalMeetings] = useState<number>(0);
  const [loadingInvestors, setLoadingInvestors] = useState<boolean>(true);
  const [loadingMeetings, setLoadingMeetings] = useState<boolean>(true);
  const [loadingStats, setLoadingStats] = useState<boolean>(true);
  const [statsLoadFailed, setStatsLoadFailed] = useState<boolean>(false);
  const [showInitialDashboardLoader, setShowInitialDashboardLoader] =
    useState<boolean>(isInitialDashboardLoad);
  const [hasConnectedCalendar, setHasConnectedCalendar] = useState<boolean>(false);
  // Stats state
  const [stats, setStats] = useState({
    totalAUM: 0,
    revenue: 0,
  });
  const isDashboardDataLoading = loadingInvestors || loadingMeetings || loadingStats;
  const initialDashboardLoaderBranding = useMemo(
    () => buildPageLoaderBranding(profileData) ?? initialDashboardBranding,
    [initialDashboardBranding, profileData],
  );
  const initialDashboardLoaderMessages = useMemo(
    () =>
      isInitialDashboardLoad
        ? buildInitialAppLoadPageLoaderMessages({
            advisorName: suppressInitialDashboardAdvisorName
              ? undefined
              : (initialDashboardAdvisorName ??
                getInitialAppLoadAdvisorName(location.state) ??
                getAdvisorNameFromUser(user)),
            destination: 'dashboard',
            destinationMessages: getDashboardInitialLoadCheckpointMessages({
              loadingInvestors,
              loadingMeetings,
              loadingStats,
            }),
          })
        : DASHBOARD_PAGE_LOADER_MESSAGES,
    [
      initialDashboardAdvisorName,
      isInitialDashboardLoad,
      loadingInvestors,
      loadingMeetings,
      loadingStats,
      location.state,
      suppressInitialDashboardAdvisorName,
      user,
    ],
  );

  useEffect(() => {
    if (!locationHasInitialAppLoad) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: clearInitialAppLoadState(location.state),
      },
    );
  }, [
    locationHasInitialAppLoad,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  const handleInitialDashboardLoaderHandoffComplete = useCallback(() => {
    setShowInitialDashboardLoader(false);
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        await getUserData();
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, []);

  // Fetch financial stats
  useEffect(() => {
    const fetchStats = async () => {
      setLoadingStats(true);
      setStatsLoadFailed(false);
      try {
        const stats: CorrespondentStats = await getCorrespondentStats();
        setStats({
          totalAUM: parseDashboardStat(stats.aum),
          revenue: parseDashboardStat(stats.total_fee),
        });
      } catch (error) {
        setStatsLoadFailed(true);
        console.error('Error fetching financial stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchStats();
  }, []);

  // Fetch investors data
  const fetchAllInvestorList = useCallback(async () => {
    try {
      let totalCount = 0;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const investorsData = await getInvestors({
          limit: INVESTOR_COUNT_PAGE_SIZE,
          offset,
        });

        totalCount += investorsData.length;
        offset += investorsData.length;
        hasMore = investorsData.length === INVESTOR_COUNT_PAGE_SIZE;
      }

      setTotalInvestors(totalCount);
    } catch (error) {
      console.error('Error fetching investors:', error);
    } finally {
      setLoadingInvestors(false);
    }
  }, []);

  useEffect(() => {
    fetchAllInvestorList();
  }, [fetchAllInvestorList]);

  // Check if a date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Fetch upcoming meetings
  useEffect(() => {
    const fetchUpcomingMeetings = async () => {
      setLoadingMeetings(true);
      try {
        // Check if user has connected their calendar
        const accountsData: GetGoogleAccountResponse = await getConnectedGoogleAccounts();
        const hasCalendar = accountsData.google_accounts.length > 0;
        setHasConnectedCalendar(hasCalendar);

        if (!hasCalendar) {
          setUpcomingMeeting(null);
          setTotalMeetings(0);
          return;
        }

        // Get scheduled meetings
        const meetingsData: GoogleCalendarEventResponse = await getGoogleCalendarEvents();
        const allMeetings = meetingsData.scheduled_meetings;

        // Filter for today's meetings
        const todayMeetings = allMeetings.filter((meeting) => {
          const meetingDate = new Date(meeting.start_time);
          return isToday(meetingDate);
        });

        // Set total meeting count for today
        setTotalMeetings(todayMeetings.length);

        // Set the soonest upcoming meeting for today (if available)
        if (todayMeetings.length > 0) {
          // Sort by start time
          const sortedMeetings = [...todayMeetings].sort(
            (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
          );

          // Find next upcoming meeting (not in the past)
          const now = new Date();
          const upcomingMeetings = sortedMeetings.filter(
            (meeting) => new Date(meeting.start_time).getTime() > now.getTime(),
          );

          if (upcomingMeetings.length > 0) {
            setUpcomingMeeting({
              title: upcomingMeetings[0].title,
              start_time: upcomingMeetings[0].start_time,
              end_time: upcomingMeetings[0].end_time,
            });
          } else if (sortedMeetings.length > 0) {
            // If no upcoming meeting, show the latest one today
            setUpcomingMeeting({
              title: sortedMeetings[sortedMeetings.length - 1].title,
              start_time: sortedMeetings[sortedMeetings.length - 1].start_time,
              end_time: sortedMeetings[sortedMeetings.length - 1].end_time,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching meetings:', error);
      } finally {
        setLoadingMeetings(false);
      }
    };

    fetchUpcomingMeetings();
  }, []);

  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format meeting time
  const getMeetingTime = () => {
    if (!upcomingMeeting) {
      return hasConnectedCalendar ? 'No meetings today' : 'Not connected';
    }
    const date = new Date(upcomingMeeting.start_time);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const quickAccessItems: QuickAccessItem[] = [
    {
      title: 'Add New Client',
      icon: <UserAddOutlined />,
      route: '/home/clients/new',
    },
    {
      title: 'View Clients',
      route: '/home/clients',
      icon: <InvestorsIcon />,
    },
    {
      title: 'Share Document',
      route: '/home/filehub',
      icon: <FileHubIcon />,
    },
    {
      title: 'Review Meeting Notes',
      route: '/home/meetinghub',
      icon: <MeetingIcon />,
    },
    {
      title: 'Manage Team Members',
      route: '/home/members',
      icon: <TeamIcon />,
    },
    {
      title: 'Customize Branding',
      route: '/home/company-settings',
      icon: <SettingsIcon />,
    },
    ...(!isProductionEnvironment // TODO: Remove this flag once back end is released
      ? [
          {
            title: 'See transaction history',
            route: '/home/transaction-history',
            icon: <TransactionHistoryIcon />,
          },
        ]
      : []),
  ];

  const initialDashboardLoader = showInitialDashboardLoader ? (
    <WavvestPageLoader
      key="initial-app-load"
      animationStartedAt={initialDashboardLoaderStartedAt}
      branding={initialDashboardLoaderBranding}
      destination="dashboard"
      isReady={!isDashboardDataLoading}
      messages={initialDashboardLoaderMessages}
      onHandoffComplete={handleInitialDashboardLoaderHandoffComplete}
      showFallbackBranding={!initialDashboardLoaderBranding}
    />
  ) : null;

  if (showInitialDashboardLoader && isDashboardDataLoading) {
    return initialDashboardLoader;
  }

  return (
    <>
      <div className="dashboard">
        <div className="dashboard-layout">
          <Row gutter={[16, 16]} className="dashboard-row">
            <Col xs={24} md={12}>
              <Card variant="borderless" className="stat-card">
                <Statistic
                  title="Total Clients"
                  value={totalInvestors}
                  valueStyle={{ fontSize: '32px', fontWeight: 600 }}
                  precision={0}
                  loading={loadingInvestors}
                />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card variant="borderless" className="stat-card">
                <Statistic
                  title="Total AUM"
                  value={statsLoadFailed ? 'Unavailable' : formatCurrency(stats.totalAUM)}
                  precision={2}
                  valueStyle={{ fontSize: '32px', fontWeight: 600 }}
                  loading={loadingStats}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} className="dashboard-row">
            <Col xs={24} md={12}>
              <QuickAccess items={quickAccessItems} />
            </Col>
            <Col xs={24} md={12}>
              <Card variant="borderless" className="stat-card">
                <Statistic
                  title="Upcoming meetings today"
                  value={getMeetingTime()}
                  valueStyle={{ fontSize: '20px', fontWeight: 600 }}
                  suffix={<Text>Total meetings: {totalMeetings}</Text>}
                  loading={loadingMeetings}
                />
              </Card>
            </Col>
          </Row>
        </div>
      </div>
      {initialDashboardLoader}
    </>
  );
};

export default Dashboard;
